import { useContext, useEffect, useState } from 'react';
import {
    Badge, Collapse, Drawer, List, ListItem, ListItemIcon, ListItemText,
    IconButton, Box, Typography, Tooltip,
    useMediaQuery, useTheme,
} from '@mui/material';
import InventoryService from '../services/InventoryService';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import menuItems from '../config/menuConfig';
import { useTranslation } from 'react-i18next';
import logo from '../images/CRM-Logo-large.png';

const OPEN_WIDTH = 256;
const CLOSED_WIDTH = 56;
export const MOBILE_TOPBAR_HEIGHT = 56;

// Flat crisp white sidebar — no background gradient; the brand teal #427898 shows
// only as accents (icons + the active pill/bar).
const SIDEBAR_BG   = '#ffffff';
const ACTIVE_BG    = 'rgba(66,120,152,0.10)';
const HOVER_BG     = 'rgba(66,120,152,0.07)';
const TEXT         = '#475569';
const ACTIVE_TEXT  = '#427898';
const ICON_DIM     = '#427898';
const ICON_BRIGHT  = '#427898';
const DIVIDER      = 'rgba(30,58,82,0.09)';
// The signature gradient — brand teal, used on the active accent bar
const ACCENT_GRAD  = 'linear-gradient(180deg, #4e90b5 0%, #427898 100%)';
// Muted version of the accent, used as the guide line beside group children
const GUIDE_GRAD   = 'linear-gradient(180deg, rgba(66,120,152,0.30) 0%, rgba(66,120,152,0.16) 100%)';

const itemSx = (isActive) => ({
    borderRadius: '10px',
    mb: 0.5,
    justifyContent: 'center',
    background: isActive ? ACTIVE_BG : 'transparent',
    position: 'relative',
    overflow: 'hidden',
    transition: 'background 0.15s',
    '&:hover': { background: isActive ? ACTIVE_BG : HOVER_BG },
    '&::before': isActive ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '18%',
        height: '64%',
        width: '3px',
        background: ACCENT_GRAD,
        borderRadius: '0 3px 3px 0',
        boxShadow: '2px 0 8px rgba(66,120,152,0.22)',
    } : {},
});

/* ── flat nav item ── */
const FlatItem = ({ item, expanded, location, navigate, onItemClick, stockAlertCount, t }) => {
    const isActive = location.pathname === item.path;
    const label = t(`menu.${item.label}`, item.label);
    const badgeCount = item.showStockBadge ? stockAlertCount : 0;

    return (
        <Tooltip title={!expanded ? label : ''} placement="right" arrow>
            <ListItem
                button
                onClick={() => { navigate(item.path); onItemClick?.(); }}
                sx={{ ...itemSx(isActive), px: expanded ? 1.75 : 0 }}
            >
                <ListItemIcon sx={{ color: isActive ? ICON_BRIGHT : ICON_DIM, minWidth: expanded ? 36 : 'unset', justifyContent: 'center' }}>
                    <Badge badgeContent={badgeCount} color="error" max={9}>
                        {item.icon}
                    </Badge>
                </ListItemIcon>
                {expanded && (
                    <ListItemText
                        primary={label}
                        primaryTypographyProps={{ fontSize: 13.5, fontWeight: isActive ? 600 : 400, color: isActive ? ACTIVE_TEXT : TEXT, letterSpacing: 0.15 }}
                    />
                )}
            </ListItem>
        </Tooltip>
    );
};

/* ── group nav item (collapsible) ── */
const GroupItem = ({ item, expanded, location, navigate, onItemClick, role, t, stockAlertCount, onExpandSidebar }) => {
    const visibleChildren = item.children.filter(c => c.roles.includes(role));
    const hasActiveChild = visibleChildren.some(c => location.pathname === c.path);
    const groupBadgeCount = visibleChildren.some(c => c.showStockBadge) ? stockAlertCount : 0;

    const [open, setOpen] = useState(hasActiveChild);
    const groupLabel = t(`menu.${item.label}`, item.label);

    const handleGroupClick = () => {
        if (!expanded) {
            // Collapsed sidebar: open it first, then expand the group
            onExpandSidebar?.();
            setOpen(true);
        } else {
            setOpen(o => !o);
        }
    };

    return (
        <>
            <Tooltip title={!expanded ? groupLabel : ''} placement="right" arrow>
                <ListItem
                    button
                    onClick={handleGroupClick}
                    sx={{ ...itemSx(hasActiveChild && !open), px: expanded ? 1.75 : 0 }}
                >
                    <ListItemIcon sx={{ color: hasActiveChild ? ICON_BRIGHT : ICON_DIM, minWidth: expanded ? 36 : 'unset', justifyContent: 'center' }}>
                        <Badge
                            variant="dot"
                            color="error"
                            invisible={!groupBadgeCount || (expanded && open)}
                        >
                            {item.icon}
                        </Badge>
                    </ListItemIcon>
                    {expanded && (
                        <>
                            <ListItemText
                                primary={groupLabel}
                                primaryTypographyProps={{ fontSize: 13.5, fontWeight: hasActiveChild ? 600 : 400, color: hasActiveChild ? ACTIVE_TEXT : TEXT, letterSpacing: 0.15 }}
                            />
                            {open ? <ExpandLessIcon sx={{ color: ICON_DIM, fontSize: 18 }} /> : <ExpandMoreIcon sx={{ color: ICON_DIM, fontSize: 18 }} />}
                        </>
                    )}
                </ListItem>
            </Tooltip>

            {expanded && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List
                        disablePadding
                        sx={{
                            ml: '27px',
                            pl: 1,
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 4,
                                bottom: 8,
                                width: '2px',
                                borderRadius: '2px',
                                background: GUIDE_GRAD,
                            },
                        }}
                    >
                        {visibleChildren.map(child => {
                            const isActive = location.pathname === child.path;
                            const childLabel = t(`menu.${child.label}`, child.label);
                            const badgeCount = child.showStockBadge ? stockAlertCount : 0;
                            return (
                                <ListItem
                                    key={child.path}
                                    button
                                    onClick={() => { navigate(child.path); onItemClick?.(); }}
                                    sx={{ ...itemSx(isActive), px: 1.5, py: 0.75 }}
                                >
                                    <ListItemText
                                        primary={childLabel}
                                        primaryTypographyProps={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? ACTIVE_TEXT : TEXT, letterSpacing: 0.1 }}
                                    />
                                    {badgeCount > 0 && (
                                        <Box sx={{
                                            ml: 1, px: 0.75, minWidth: 18, height: 18, flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            borderRadius: '9px', background: '#ef4444',
                                            color: 'white', fontSize: 10.5, fontWeight: 700, lineHeight: 1,
                                        }}>
                                            {badgeCount > 9 ? '9+' : badgeCount}
                                        </Box>
                                    )}
                                </ListItem>
                            );
                        })}
                    </List>
                </Collapse>
            )}
        </>
    );
};

/* ── nav items list ── */
const NavItems = ({ expanded, onItemClick, location, navigate, role, t, stockAlertCount, onExpandSidebar }) => {
    const visible = menuItems.filter(item => {
        if (item.type === 'divider') return true;
        if (item.type === 'group') {
            return item.roles.includes(role) && item.children.some(c => c.roles.includes(role));
        }
        return item.roles.includes(role);
    });

    // Role filtering can leave dividers leading, trailing, or back-to-back — drop those
    const filtered = visible.filter((item, i) => {
        if (item.type !== 'divider') return true;
        const prev = visible[i - 1];
        const hasItemAfter = visible.slice(i + 1).some(n => n.type !== 'divider');
        return prev && prev.type !== 'divider' && hasItemAfter;
    });

    return (
        <List sx={{ px: 1, pt: 1.5 }}>
            {filtered.map((item, idx) => {
                if (item.type === 'divider') {
                    return (
                        <Box
                            key={`divider-${idx}`}
                            sx={{ height: '1px', background: DIVIDER, my: 1.25, mx: expanded ? 1 : 0.5 }}
                        />
                    );
                }
                if (item.type === 'group') {
                    return (
                        <GroupItem
                            key={item.label}
                            item={item}
                            expanded={expanded}
                            location={location}
                            navigate={navigate}
                            onItemClick={onItemClick}
                            role={role}
                            t={t}
                            stockAlertCount={stockAlertCount}
                            onExpandSidebar={onExpandSidebar}
                        />
                    );
                }
                return (
                    <FlatItem
                        key={item.path}
                        item={item}
                        expanded={expanded}
                        location={location}
                        navigate={navigate}
                        onItemClick={onItemClick}
                        stockAlertCount={stockAlertCount}
                        t={t}
                    />
                );
            })}
        </List>
    );
};

/* ── main component ── */
const NavMenu = () => {
    const navigate  = useNavigate();
    const location  = useLocation();
    const { role }  = useContext(AuthContext);
    const { t }     = useTranslation();
    const theme     = useTheme();
    const isMobile  = useMediaQuery(theme.breakpoints.down('md'));

    const [drawerOpen, setDrawerOpen] = useState(localStorage.getItem('NAV_BAR_OPEN') === 'true');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [stockAlertCount, setStockAlertCount] = useState(0);

    useEffect(() => {
        if (!role) return;
        InventoryService.getAlerts()
            .then(alerts => setStockAlertCount(alerts?.length ?? 0))
            .catch(() => {});
    }, [role]);

    const toggleDrawer = () => {
        const next = !drawerOpen;
        localStorage.setItem('NAV_BAR_OPEN', next ? 'true' : 'false');
        setDrawerOpen(next);
    };

    const expandSidebar = () => {
        if (!drawerOpen) {
            localStorage.setItem('NAV_BAR_OPEN', 'true');
            setDrawerOpen(true);
        }
    };

    const sharedProps = { location, navigate, role, t, stockAlertCount };

    /* ── MOBILE ── */
    if (isMobile) {
        return (
            <>
                {/* Fixed top bar */}
                <Box sx={{
                    position: 'fixed', top: 0, left: 0, right: 0,
                    height: MOBILE_TOPBAR_HEIGHT,
                    background: SIDEBAR_BG,
                    borderBottom: `1px solid ${DIVIDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 2,
                    zIndex: (th) => th.zIndex.appBar,
                }}>
                    <img src={logo} alt="Plankton CRM" style={{ width: 120, height: 'auto' }} />
                    <IconButton onClick={() => setMobileOpen(true)} sx={{ color: ICON_DIM, '&:hover': { color: ACTIVE_TEXT, backgroundColor: HOVER_BG } }}>
                        <MenuIcon />
                    </IconButton>
                </Box>

                {/* Left-slide overlay drawer */}
                <Drawer
                    anchor="left"
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{
                        sx: {
                            width: OPEN_WIDTH,
                            background: SIDEBAR_BG,
                            display: 'flex', flexDirection: 'column',
                        }
                    }}
                >
                    {/* Drawer header */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, height: 64,
                        borderBottom: `1px solid ${DIVIDER}`,
                        flexShrink: 0,
                    }}>
                        <img
                            src={logo}
                            alt="Plankton CRM"
                            style={{ width: 130 }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => setMobileOpen(false)}
                            sx={{ color: ICON_DIM, '&:hover': { color: ACTIVE_TEXT, backgroundColor: HOVER_BG } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                        <NavItems {...sharedProps} expanded onItemClick={() => setMobileOpen(false)} />
                    </Box>
                </Drawer>
            </>
        );
    }

    /* ── DESKTOP ── */
    return (
        <Drawer
            variant="permanent"
            sx={{
                width: drawerOpen ? OPEN_WIDTH : CLOSED_WIDTH,
                flexShrink: 0,
                transition: 'width 0.3s ease',
                '& .MuiDrawer-paper': {
                    width: drawerOpen ? OPEN_WIDTH : CLOSED_WIDTH,
                    background: SIDEBAR_BG,
                    boxSizing: 'border-box',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: '1px solid #e6ecf1',
                    boxShadow: '4px 0 24px -12px rgba(30,58,82,0.12)',
                    transition: 'width 0.3s ease',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: drawerOpen ? 'space-between' : 'center',
                px: drawerOpen ? 2.5 : 1,
                height: 64,
                flexShrink: 0,
                borderBottom: `1px solid ${DIVIDER}`,
            }}>
                {drawerOpen && (
                    <img
                        src={logo}
                        alt="Plankton CRM"
                        style={{ width: 140 }}
                    />
                )}

                <IconButton
                    onClick={toggleDrawer}
                    size="small"
                    sx={{
                        width: 30, height: 30,
                        backgroundColor: 'rgba(66,120,152,0.10)',
                        color: ICON_DIM,
                        transition: 'transform 0.3s ease, background-color 0.15s',
                        transform: drawerOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                        '&:hover': { backgroundColor: 'rgba(66,120,152,0.18)', color: ACTIVE_TEXT },
                    }}
                >
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Nav items */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <NavItems {...sharedProps} expanded={drawerOpen} onExpandSidebar={expandSidebar} />
            </Box>
        </Drawer>
    );
};

export default NavMenu;
